import { AngularFireAuth } from '@angular/fire/compat/auth';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  Auth,
  GoogleAuthProvider, UserCredential, sendSignInLinkToEmail, signInWithEmailLink, signInWithRedirect
} from "firebase/auth";
import { EncryptionService } from './encryption.service';
import { getAuth, signInWithPopup, OAuthProvider } from "firebase/auth";
import { FirebaseError, initializeApp } from 'firebase/app';
import { DataService } from './data.service';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';
import { SessionService } from './session.service';
import { environment } from 'src/environments/environment';
import { SafeStorage } from '../utils/safe-storage';
import { rememberNativeApp } from '../utils/native-app';

import { AnalyticsService } from './analytics.service';
import { AdsConversionService } from './ads-conversion.service';
import { reloadApp } from '../utils/reload-app';

declare let fbq: (...args: any[]) => void;
declare let gtag: (...args: any[]) => void;

@Injectable({
  providedIn: "root",
})
export class AuthService {

  userData: any; // Save logged in user data
  encryptedData: any;
  decryptedData: any;
  private userId !: string;
  private token !: string;
  redirectUrl: string | undefined;
  private app = initializeApp(environment.firebase);
  private auth: Auth = getAuth(this.app);
  constructor(private http: HttpClient, private dataService: DataService, private sessionService: SessionService,
    private afAuth: AngularFireAuth, private encryptionService: EncryptionService, private analytics: AnalyticsService,
    private adsConversion: AdsConversionService) {
    this.afAuth.authState.subscribe((user) => {
      try {
        if (user) {
          this.userData = user;
          this.encryptedData = this.encryptionService.encryptData(JSON.stringify(this.userData._delegate));
          SafeStorage.setItem('user', this.encryptedData);
          this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
          JSON.parse(this.decryptedData!);
        }
        else {
          SafeStorage.setItem('user', 'null');
          JSON.parse(SafeStorage.getItem('user') ?? 'null');
        }
      } catch {
        // If storage is unavailable, continue without persisting auth state.
        // The user will still see the app instead of a blank page.
      }
    });
  }

  // loginWithToken(token: string): Observable<any> {
  //   return this.http.post<any>('/api/auth/verify-token', { token }).pipe(
  //     tap(user => {
  //       localStorage.setItem('jwt', token);
  //       this.userSubject.next(user);
  //     }),
  //     catchError(error => {
  //       console.error('Token validation failed', error);
  //       return of(null);
  //     })
  //   );
  // }

  // Reactive Firebase auth state. Emits the current user (or null) as soon as
  // Firebase resolves it — used to await auth readiness without polling
  // localStorage. AngularFire replays the latest value to new subscribers, so
  // late subscribers still get the already-resolved state immediately.
  get authState$(): Observable<any> {
    return this.afAuth.authState;
  }

  isAuthenticated(): boolean {
    return !!SafeStorage.getItem('user');
  }

  getToken(): string | null {
    return SafeStorage.getItem('user');
  }


  /**
   * Firebase's own "popup was closed" detection polls the popup window and can
   * silently never fire — seen when Cross-Origin-Opener-Policy (or similar
   * cross-origin isolation) stops this window from reading `.closed` on the
   * popup it opened. When that happens the sign-in promise never settles, and
   * every caller (login page, login popup, deferred sign-in) is left showing
   * its loader forever with no way out but a refresh.
   *
   * Between 2026-09-17 and 2026-09-23 this was worked around by watching for a
   * `focus` event on this window and calling it a cancel. Don't bring that
   * back. A focus event is not proof the popup closed — on mobile, app
   * switching, the virtual keyboard and the browser's own chrome all fire it
   * with the popup still working — and the guess was wrong often enough to cut
   * real signups from 8-11 a day to 1-3 while telling those people they had
   * cancelled.
   *
   * So nothing here guesses any more. If the popup result simply never comes,
   * the page reloads. A reload cannot be wrong about what happened: whatever
   * Firebase really did is persisted, so a sign-in that actually succeeded
   * comes back signed in, and one that was actually cancelled comes back to a
   * clean page with the quick-create draft restored. It costs the visitor a
   * blink instead of a dead loader.
   *
   * The wait before reloading is deliberately long. Reloading mid-sign-in
   * orphans the popup and makes them start over, and a real sign-in behind a
   * password manager, an emailed code or an authenticator app can take more
   * than a minute. Firebase's own cancel detection, when it works at all,
   * fires in a second or two and settles the promise long before this.
   */
  private awaitPopupResult<T>(popup: Promise<T>): Promise<T> {
    const POPUP_TIMEOUT_MS = 90000;

    return new Promise<T>((resolve, reject) => {
      const timeout = setTimeout(() => this.recoverByReload(), POPUP_TIMEOUT_MS);
      popup.then(
        (value) => { clearTimeout(timeout); resolve(value); },
        (err) => { clearTimeout(timeout); reject(err); },
      );
    });
  }

  /**
   * Last resort for a sign-in that never came back: start the page over rather
   * than sit on a loader. Deliberately not a rejection — claiming a cancel we
   * cannot see is exactly what broke signups in September.
   */
  private recoverByReload(): void {
    try {
      this.analytics.capture('signin_popup_reload', { signed_in: !!this.signedInUid() });
    } catch {
      // Never let telemetry stand between the visitor and a working page.
    }
    // A beat for that capture to leave the tab before the document goes away.
    setTimeout(() => reloadApp('signin_popup_timeout', { force: true }), 400);
  }

  /**
   * The uid Firebase currently holds, ignoring the anonymous identity a guest
   * may already be carrying. Both app instances are checked because the Apple
   * path signs in against the default app and the Google path against ours.
   */
  private signedInUid(): string | null {
    try {
      const user = this.auth.currentUser ?? getAuth().currentUser;
      return user && !user.isAnonymous ? user.uid : null;
    } catch {
      return null;
    }
  }

  /**
   * `keepCurrentSession` is for a guest signing in from inside an event: the
   * popup's sign-in replaces their session on success anyway, and signing out
   * up front meant a cancelled popup left them with no session at all.
   */
  signUpWithGoogle(options: { keepCurrentSession?: boolean } = {}) {
    // console.log('auth service:Signing up with Google...'); //__CONSOLE_LOG__
    if (!options.keepCurrentSession) {
      this.afAuth.signOut();
    }
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    return this.awaitPopupResult(this.afAuth.signInWithPopup(provider));
  }

  async signUpWithApple() {
    // console.log('auth service:Signing up with Apple...'); //__CONSOLE_LOG__

    const provider = new OAuthProvider('apple.com');
    provider.addScope('name');
    provider.addScope('email');

    // console.log(provider.getScopes() //__CONSOLE_LOG__)
    const auth = getAuth();  // return this.afAuth.signInWithPopup(provider);

    return this.awaitPopupResult(signInWithPopup(auth, provider)).then((result) => {
      // The signed-in user info.
      const user = result.user;
      // console.log({ user }); //__CONSOLE_LOG__
      this.encryptedData = this.encryptionService.encryptData(JSON.stringify(user));
      SafeStorage.setItem('user', this.encryptedData);
      return result;
    });
  }

  getAuthInstance(): Auth {
    return this.auth;
  }

  signInAnonymously() {
    return this.afAuth.signInAnonymously();
  }

  checkIfMobileExist(mobile: string) {
    const params = new HttpParams().set("mobile", mobile);
    this.encryptedData = SafeStorage.getItem('user');
    this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
    const user = JSON.parse(this.decryptedData || '{}');
    const token: any = user?.stsTokenManager.accessToken;
    // set headers
    const headers = new HttpHeaders().set(
      "Content-Type",
      "application/x-www-form-urlencoded"
    ).set(
      "Authorization", token,
    );

    return this.http.post(
      "https://eou9qlevsf.execute-api.us-west-1.amazonaws.com/check_mobile",
      params.toString(),
      { headers }
    );
  }

  signUp() {
    this.encryptedData = SafeStorage.getItem('user');
    this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
    const user = JSON.parse(this.decryptedData || '{}');
    const token: any = user?.stsTokenManager.accessToken;
    const headers = new HttpHeaders().set(
      "Content-Type",
      "application/x-www-form-urlencoded"
    ).set(
      "Authorization", token,
    );

    // set headers
    // Google Login -> no MFA Error -> Signup API -> enter mobile number -> checkifExist -> OTP -> enroll for MFA -> Check for new token

    return this.http.post(
      "https://60wv5n1188.execute-api.us-west-1.amazonaws.com/sign-up", '', { headers }
    );
  }

  async sendSignInLink(email: any, deepLink: any) {
    const auth = getAuth();
    const actionCodeSettings = {
      url: deepLink,
      handleCodeInApp: true,
      dynamicLinkDomain: 'picaggopwa.page.link'
    };

    // console.log(deepLink); //__CONSOLE_LOG__

    const emailBody = deepLink;
    //sendSignInLinkToEmail(auth, email,actionCodeSettings);
    this.afAuth.sendSignInLinkToEmail(email, actionCodeSettings)
      .then(() => {
        // console.log('Sign-in link sent to email successfully'); //__CONSOLE_LOG__
        // You can show a confirmation message to the user
      })
      .catch((error: FirebaseError) => {
        // console.error('Error sending sign-in link:', error.code, error.message, error.name); //__CONSOLE_LOG__
        // Handle error, e.g., display an error message to the user
      });


  }

  signInwithLink(auth: any, email: any) {
    signInWithEmailLink(auth, email, window.location.href)
      .then((result) => {
        // Clear email from storage.
        SafeStorage.removeItem('emailForSignIn');
        // The signed-in user info.
        const user = result.user;
        // console.log({ result }); //__CONSOLE_LOG__
        this.encryptedData = this.encryptionService.encryptData(JSON.stringify(user));
        SafeStorage.setItem('user', this.encryptedData);
        const credential = OAuthProvider.credentialFromResult(result);
        const accessToken = credential?.accessToken;
        const idToken = credential?.idToken;
      })
      .catch((error) => {
        // console.log(error); //__CONSOLE_LOG__

        // Some error occurred, you can inspect the code: error.code
        // Common errors could be invalid email and invalid or expired OTPs.
      });
  }

  // The photo travels to pwa_sign_in as a request HEADER, and API Gateway rejects
  // any request whose headers total more than ~10 KB (431/400, with no CORS headers,
  // so the browser reports "0 Unknown Error" and sign-in fails). A full Google photo
  // can push it over, so it is shrunk to a small JPEG first, and dropped entirely if
  // it is still too big: a missing profile photo must never cost a sign-in.
  private static readonly PROFILE_IMAGE_MAX_PX = 96;
  private static readonly PROFILE_IMAGE_MAX_CHARS = 6000;

  async getProfileImageBase64(photoURL: string): Promise<string | null> {
    if (!photoURL) return null;

    try {
      const response = await fetch(photoURL);
      const blob = await response.blob();

      const bitmap = await createImageBitmap(blob);
      const scale = Math.min(1, AuthService.PROFILE_IMAGE_MAX_PX / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close?.();

      // Same data-URL shape as before, so pwa_sign_in's saveProfilePic is unchanged.
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      return dataUrl.length <= AuthService.PROFILE_IMAGE_MAX_CHARS ? dataUrl : null;

    } catch (err) {
      // console.error('Error fetching or converting profile image:', err); //__CONSOLE_LOG__
      return null;
    }
  }



  async signIn(user: any, mobile: any, isNewUser: boolean = false) {
    // console.log('auth service:Signin'); //__CONSOLE_LOG__
    // console.log(user); //__CONSOLE_LOG__
    const profileImageBase64 = await this.getProfileImageBase64(user.photoURL);
    // console.log('Profile image Base64:', profileImageBase64); //__CONSOLE_LOG__
    let token;
    if (SafeStorage.getItem('accessToken')) {
      token = SafeStorage.getItem('accessToken');
    } else {
      token = user?.stsTokenManager.accessToken;
    }
    // console.log(mobile); //__CONSOLE_LOG__
    let params;
    // if(mobile != undefined)
    // {
    //    params = new HttpParams().set("device", 'ios').set("notification_token", '').set("device_name", 'browser').set("version", "aws").set("mobile",mobile);
    // }
    // else
    // {
    params = new HttpParams().set("device", 'ios').set("notification_token", '').set("device_name", 'browser').set("version", "aws");
    // }
    // const params = new HttpParams().set("device", 'ios').set("notification_token", '').set("device_name", 'browser').set("version", "aws").set("mobile",mobile);
    const headers = new HttpHeaders().set(
      "Content-Type",
      "application/x-www-form-urlencoded"
    ).set(
      "Authorization", token,
    );

    const options = {
      headers: new HttpHeaders().set("Content-Type", "application/x-www-form-urlencoded")
        .set("Authorization", token)
        .set("hex", SafeStorage.getItem("eventId") || "{}")
        .set("device", 'ios')
        .set("notification_token", ' ')
        .set("device_name", 'browser')
        .set("version", 'pwa_v1')
        .set("image", profileImageBase64 || '')
    };

    return this.http.get(
      //"https://vq8w0bp7ee.execute-api.us-west-1.amazonaws.com/sign-in",
      "https://hhsm3bh0af.execute-api.us-west-1.amazonaws.com/pwa_sign_in",
      options
    ).pipe(
      tap((res: any) => {
        fbq('trackCustom', 'pwa_sign_in');
        // Lets get-the-app prompts skip people who already have the app.
        rememberNativeApp(res);
        if (isNewUser) {
          // Meta Pixel CompleteRegistration plus the server report, sharing one id
          // so Meta counts it once. The server only records it once the account
          // really exists.
          this.adsConversion.track('signup');
          // Send hashed email for enhanced conversions (helps Google Ads match
          // cross-session sign-ups back to the original ad click server-side)
          if (user?.email) {
            gtag('set', 'user_data', { email: user.email });
          }
          gtag('event', 'sign_up', {
            gclid: SafeStorage.getItem('gclid') || undefined,
            fbclid: SafeStorage.getItem('fbclid') || undefined,
            has_paid_attribution: !!(SafeStorage.getItem('gclid') || SafeStorage.getItem('fbclid')),
          });
          // Direct Google Ads conversion tag — reads _gcl_aw cookie which survives
          // the OAuth redirect, giving proper campaign attribution
          gtag('event', 'conversion', {
            send_to: 'AW-18087789450/93KjCP-C_MscEIqH97BD',
            transaction_id: user?.uid,
          });
        }

        // PostHog: identify with analytics_id (no PII — matches iOS implementation)
        const analyticsId = res?.analytics_id || res?.account?.analytics_id;
        if (analyticsId) {
          SafeStorage.setItem('analytics_id', analyticsId);
        }
        // Only ever identify with analytics_id. Never fall back to the Firebase uid:
        // that pollutes PostHog with a second identity (the account details fetch later
        // re-identifies with the real analytics_id, so the same session ends up split
        // across a uid person and an analytics_id person).
        // No reset() here either — reset() generates a fresh anonymous id and breaks the
        // anonymous -> identified merge, orphaning pre-login events. reset() belongs only
        // in signOut() (clears the previous user's identity).
        const posthogId = analyticsId || SafeStorage.getItem('analytics_id');
        if (posthogId) {
          this.analytics.identify(posthogId);
        }
        this.analytics.capture(isNewUser ? 'signup_completed' : 'login_completed', {
          signup_method: user?.providerData?.[0]?.providerId ?? 'unknown',
          gclid: SafeStorage.getItem('gclid') || undefined,
          fbclid: SafeStorage.getItem('fbclid') || undefined,
          utm_source: SafeStorage.getItem('utm_source') || undefined,
          utm_medium: SafeStorage.getItem('utm_medium') || undefined,
          utm_campaign: SafeStorage.getItem('utm_campaign') || undefined,
          utm_content: SafeStorage.getItem('utm_content') || undefined,
          utm_term: SafeStorage.getItem('utm_term') || undefined,
        });

        // Clarity: tag the session with the user ID so recordings are searchable
        // by user even when the session started anonymously on the login page.
        const clarityId = posthogId;
        if (clarityId && (window as any).clarity) {
          (window as any).clarity('identify', clarityId);
          if (isNewUser) {
            (window as any).clarity('set', 'user_type', 'new_user');
          }
        }
      })
    );
  }

  signOut() {
    this.dataService.showTopBar = false;
    this.sessionService.endSession();
    this.analytics.reset();
    SafeStorage.clear();
    SafeStorage.removeItem('user'); // Retain guest user status
    // In-app browsers (Facebook, Instagram) have no navigator.serviceWorker at all,
    // and reading .controller off undefined used to abort sign-out midway.
    const swController = navigator.serviceWorker?.controller;
    if (swController) {
      // console.log('Sending message to service worker to clear cache'); //__CONSOLE_LOG__ //__CONSOLE_LOG__
      swController.postMessage({ type: 'CLEAR_CACHE' });
    }
    return this.afAuth.signOut();
  }


  sendEmail(email: any, emailBody: any) {
    const headers = new HttpHeaders()
      .set("Content-Type", "application/x-www-form-urlencoded")
    const params = new HttpParams()
      .set("email", email.toString())
      .set("emailBody", emailBody.toString())

    // console.log({ params }); //__CONSOLE_LOG__

    return this.http.post(
      "https://tje0qz989k.execute-api.us-west-1.amazonaws.com/pwa_send_emails",
      params.toString(),
      { headers }
    );

  }


  anonymousToGoogle() {
    // const app = initializeApp(environment.firebase);
    // const auth = getAuth(app);
    SafeStorage.setItem('showLoader', "true");
    // return signOut(auth);
    SafeStorage.removeItem('user');
    return this.afAuth.signOut();
  }


  async getUserId(): Promise<string | undefined> {
    const data: any = await this.afAuth.currentUser;
    // console.log({ data }); //__CONSOLE_LOG__
    this.userId = data?.uid;
    return this.userId;
  }

  async getfbToken(): Promise<string | undefined> {
    const data: any = await this.afAuth.currentUser;
    // console.log({ data }); //__CONSOLE_LOG__
    this.token = data?.stsTokenManager.accessToken;
    return this.token;
  }

  async getUserData(): Promise<any> {
    this.encryptedData = SafeStorage.getItem('user');
    this.decryptedData = this.encryptionService.decryptData(this.encryptedData);
    const user = JSON.parse(this.decryptedData || '{}');
    return user;
  }


}